<script setup lang="ts">
  import { computed, defineProps, type PropType, ref, watch } from 'vue'
  import { type RuleExpression, useField } from 'vee-validate'
  import { useTranslation } from '@/composables/useTranslation'
  import { Upload, File, Trash2, Eye } from 'lucide-vue-next'
  import { useThemeColor } from '@/composables/useThemeColorV3'
  import { Button } from '@/components/ui/button'

  const { t } = useTranslation({
    en: {
      dropFilesHere: 'Drop your files here or ',
      browse: 'browse',
      allowedFiles: 'Allowed file extensions: {allowedFileExtensions}',
      fileDetails: '{type}, {size} bytes',
      filesUploaded: '{count} files uploaded',
      removeFile: 'Remove file',
      clearAll: 'Clear all',
      maximumFiles: 'Maximum {maxFiles} files',
      maximumFile: 'Maximum {maxFiles} file'
    },
    de: {
      dropFilesHere: 'Ziehen Sie Ihre Dateien hier in das Fenster oder ',
      browse: 'durchsuchen',
      allowedFiles: 'Erlaubte Dateien: {allowedFileExtensions}',
      fileDetails: '{type}, {size} Bytes',
      filesUploaded: '{count} Dateien hochgeladen',
      removeFile: 'Datei entfernen',
      clearAll: 'Alle löschen',
      maximumFiles: 'Maximal {maxFiles} Dateien',
      maximumFile: 'Maximal {maxFiles} Datei'
    }
  })

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    allowedFileExtensions: {
      type: Array,
      required: false,
      default: () => ['pdf']
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    maxFiles: {
      type: Number,
      required: false
    },
    modelValue: {
      type: Array as PropType<any[]>,
      required: false,
      default: () => []
    }
  })

  const emit = defineEmits<{
    'update:modelValue': [value: any[]]
    fileRemoved: [file: any, index: number]
  }>()

  const { value: model, errorMessage, handleBlur, meta } = useField(props.name, props.rules)
  const isDragging = ref(false)
  const fileList = ref<File[]>([])
  const fileInputRef = ref<HTMLInputElement>()
  const thumbnails = ref<Map<string, string>>(new Map())

  const themeColor = useThemeColor()

  const hasFiles = computed(() => props.modelValue && props.modelValue.length > 0)
  const canAddMore = computed(() => {
    if (!props.maxFiles) return true
    return (props.modelValue?.length || 0) < props.maxFiles
  })

  const isImageFile = (file: File | any) => {
    const type = file.type || file.mime_type || ''
    return type.startsWith('image/')
  }

  const createThumbnailFromFile = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      if (!isImageFile(file)) {
        resolve('')
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          const maxSize = 60
          let { width, height } = img

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height
              height = maxSize
            }
          }

          canvas.width = width
          canvas.height = height

          ctx?.drawImage(img, 0, 0, width, height)
          const thumbnail = canvas.toDataURL('image/jpeg', 0.8)
          resolve(thumbnail)
        }
        img.src = e.target?.result as string
      }
      reader.readAsDataURL(file)
    })
  }

  const createThumbnailFromUrl = (url: string, filename: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        const maxSize = 60
        let { width, height } = img

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width
            width = maxSize
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height
            height = maxSize
          }
        }

        canvas.width = width
        canvas.height = height

        ctx?.drawImage(img, 0, 0, width, height)
        const thumbnail = canvas.toDataURL('image/jpeg', 0.8)
        resolve(thumbnail)
      }
      img.onerror = () => {
        resolve('')
      }
      img.src = url
    })
  }

  const processExistingFiles = async (files: any[]) => {
    for (const file of files) {
      if (file.url && isImageFile(file)) {
        const thumbnail = await createThumbnailFromUrl(file.url, file.filename || file.title)
        if (thumbnail) {
          thumbnails.value.set(file.filename || file.title, thumbnail)
        }
      }
    }
  }

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = true
  }

  const handleDragLeave = () => {
    isDragging.value = false
  }

  const handleDrop = (event: DragEvent) => {
    event.preventDefault()
    isDragging.value = false
    if (event.dataTransfer && event.dataTransfer.files.length) {
      const files = Array.from(event.dataTransfer.files)
      addFiles(files)
    }
  }

  const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    if (input.files) {
      const files = Array.from(input.files)
      addFiles(files)
    }
  }

  const addFiles = async (files: File[]) => {
    if (!canAddMore.value) return

    const validFiles = files.filter((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase()
      return props.allowedFileExtensions.includes(extension || '')
    })

    const remainingSlots = props.maxFiles
      ? props.maxFiles - (props.modelValue?.length || 0)
      : validFiles.length
    const filesToAdd = validFiles.slice(0, remainingSlots)

    for (const file of filesToAdd) {
      const thumbnail = await createThumbnailFromFile(file)
      if (thumbnail) {
        thumbnails.value.set(file.name, thumbnail)
      }
    }

    fileList.value = [...fileList.value, ...filesToAdd]
    model.value = fileList.value
    emit('update:modelValue', fileList.value)
  }

  const removeFile = (index: number) => {
    const file = props.modelValue?.[index]
    if (file) {
      thumbnails.value.delete(file.name)
    }
    const updatedFiles = [...(props.modelValue || [])]
    updatedFiles.splice(index, 1)
    emit('fileRemoved', file, index)
  }

  const previewFile = (file: any) => {
    if (file.url) {
      window.open(file.url, '_blank')
    } else if (file && typeof file === 'object' && 'type' in file && 'size' in file) {
      const objectUrl = URL.createObjectURL(file as Blob)
      window.open(objectUrl, '_blank')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    }
  }

  const triggerFileInput = () => {
    if (fileInputRef.value) {
      fileInputRef.value.click()
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes'
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
    else return (bytes / 1048576).toFixed(1) + ' MB'
  }

  watch(
    () => props.modelValue,
    async (newFiles) => {
      if (newFiles && newFiles.length > 0) {
        await processExistingFiles(newFiles)
      }
    },
    { immediate: true }
  )
</script>

<template>
  <div class="space-y-2">
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium text-gray-800"
    >
      {{ label }}
    </label>

    <div
      v-if="!hasFiles || canAddMore"
      class="flex cursor-pointer justify-center rounded-xl border border-dashed bg-white p-12 transition-colors
        hover:border-gray-400"
      :class="{
        'border-2 border-red-500 focus:border-red-500 focus:ring-red-500': errorMessage,
        'border-purple-500 bg-purple-50': isDragging && !errorMessage,
        'border-gray-300 focus:border-purple-600 focus:ring-purple-600':
          !errorMessage && !isDragging
      }"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @click="triggerFileInput"
    >
      <div class="text-center">
        <Upload class="mx-auto h-12 w-12 text-gray-400" />
        <div class="mt-4 text-sm leading-6 text-gray-600">
          <div class="flex flex-wrap items-center justify-center gap-1 text-center">
            <span class="font-medium text-gray-800">
              {{ t('dropFilesHere') }}
            </span>
            <span
              :class="`font-semibold text-${themeColor}-600 hover:text-${themeColor}-700 cursor-pointer whitespace-nowrap
                hover:underline`"
            >
              {{ t('browse') }}
            </span>
          </div>
        </div>
        <p class="mt-1 px-4 text-xs text-gray-400">
          {{ t('allowedFiles', { allowedFileExtensions: allowedFileExtensions.join(', ') }) }}
        </p>
        <p
          v-if="props.maxFiles"
          class="mt-1 px-4 text-xs text-gray-400"
        >
          {{
            props.maxFiles === 1
              ? t('maximumFile', { maxFiles: props.maxFiles })
              : t('maximumFiles', { maxFiles: props.maxFiles })
          }}
        </p>
      </div>
    </div>
    <input
      ref="fileInputRef"
      class="sr-only"
      type="file"
      multiple
      :accept="props.allowedFileExtensions.map((ext) => '.' + ext).join(', ')"
      @change="handleFileChange"
      @blur="handleBlur"
    />

    <div
      v-if="hasFiles"
      class="space-y-3"
    >
      <div
        v-for="(file, index) in props.modelValue"
        :key="`${file.name}-${index}`"
        class="group relative flex items-center gap-x-3 rounded-xl border border-gray-200 bg-white p-3"
      >
        <span
          class="flex size-7 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border
            border-gray-200 bg-white text-gray-500 sm:h-[38px] sm:w-[38px]"
        >
          <img
            v-if="thumbnails.get(file.name)"
            :src="thumbnails.get(file.name)"
            :alt="file.name"
            class="h-full w-full rounded object-cover"
          />
          <File
            v-else
            class="size-3.5 flex-shrink-0 sm:size-5"
          />
        </span>

        <div class="grow truncate">
          <p class="block truncate text-sm font-semibold text-gray-800">
            {{ file.name }}
          </p>
          <p class="block truncate text-xs text-gray-500">
            {{ file.type }}, {{ formatFileSize(file.size) }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            :class="`h-8 w-8 p-0 text-${themeColor}-600 hover:bg-${themeColor}-100 hover:text-${themeColor}-700`"
            @click="previewFile(file)"
          >
            <Eye class="size-4" />
          </Button>

          <Button
            variant="secondary"
            size="sm"
            class="h-8 w-8 p-0 text-red-600 hover:bg-red-100 hover:text-red-700"
            @click="removeFile(index)"
          >
            <Trash2 class="size-4" />
          </Button>
        </div>
      </div>

      <div
        v-if="!props.maxFiles || props.maxFiles > 1"
        class="flex items-center gap-2"
      >
        <span class="text-sm text-gray-600">
          {{ t('filesUploaded', { count: props.modelValue?.length || 0 }) }}
          <span
            v-if="props.maxFiles"
            class="text-gray-400"
          >
            / {{ props.maxFiles }}
          </span>
        </span>
      </div>
    </div>

    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped></style>
